/**
 * DocumentList
 * Dokumentenliste im Ghost-Design mit CSS-Klassen.
 */

import React, { useState } from 'react'
import { useAnnotationStore } from '../../store/annotations'
import { AnnotationDocument } from '../../types/annotations'

interface DocumentListProps {
  currentCloudId?: string | null
  /** All cloud IDs in the active project */
  projectCloudIds?: string[]
  /** Called when a document should be opened (for AppShell integration) */
  onOpenDocument?: (docId: string) => void
}

export function DocumentList({ currentCloudId, projectCloudIds = [], onOpenDocument }: DocumentListProps) {
  const documents = useAnnotationStore(s => s.documents)
  const createDocument = useAnnotationStore(s => s.createDocument)
  const deleteDocument = useAnnotationStore(s => s.deleteDocument)
  const openDocumentEditor = useAnnotationStore(s => s.openDocumentEditor)
  const openDocumentViewer = useAnnotationStore(s => s.openDocumentViewer)

  const [newDocTitle, setNewDocTitle] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'title'>('updated')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const filteredDocs = documents
    .filter(d => {
      if (currentCloudId && d.cloudId) {
        // Wenn ein Projekt geladen ist: alle Clouds des Projekts akzeptieren
        if (projectCloudIds.length > 0) {
          if (!projectCloudIds.includes(d.cloudId)) return false
        } else {
          if (d.cloudId !== currentCloudId) return false
        }
      }
      if (search.trim()) {
        const q = search.toLowerCase()
        if (!d.title.toLowerCase().includes(q)) return false
      }
      return true
    })
    .sort((a, b) => {
      let cmp = 0
      if (sortBy === 'updated') cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
      else if (sortBy === 'created') cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      else cmp = a.title.localeCompare(b.title, 'de')
      return sortDir === 'desc' ? -cmp : cmp
    })

  function handleCreate() {
    const title = newDocTitle.trim() || 'Neues Dokument'
    const doc = createDocument(title, currentCloudId ?? undefined)
    setNewDocTitle('')
    setIsCreating(false)
    openDocumentEditor(doc.id)
  }

  function handleDelete(doc: AnnotationDocument, e: React.MouseEvent) {
    e.stopPropagation()
    if (confirm(`Dokument „${doc.title}" wirklich löschen?`)) {
      deleteDocument(doc.id)
    }
  }

  function handleDuplicate(doc: AnnotationDocument, e: React.MouseEvent) {
    e.stopPropagation()
    const dup = createDocument(`${doc.title} (Kopie)`, doc.cloudId ?? undefined)
    const store = useAnnotationStore.getState()
    store.updateDocument(dup.id, { content: doc.content, linkedAnnotationIds: [...doc.linkedAnnotationIds] })
    openDocumentEditor(dup.id)
  }

  return (
    <div data-component="DocumentList" className="ghost-panel">
      {/* Neues Dokument anlegen */}
      {isCreating && (
        <div data-section="new-doc-form" className="ghost-create-form">
          <input
            data-element="input-new-doc-title"
            value={newDocTitle}
            onChange={e => setNewDocTitle(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreate()
              if (e.key === 'Escape') setIsCreating(false)
            }}
            placeholder="Dokumenttitel …"
            autoFocus
            className="ghost-create-form__input"
          />
          <button
            data-element="btn-create-doc"
            onClick={handleCreate}
            className="ghost-create-form__btn"
          >
            Erstellen
          </button>
        </div>
      )}

      {/* ── Suche ─────────────────────────────────────────────────── */}
      <div data-section="doc-search" className="ghost-search">
        <div className="ghost-search__inner">
          <span className="ghost-search__icon">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
              <circle cx="7" cy="7" r="5" /><line x1="10.5" y1="10.5" x2="14" y2="14" />
            </svg>
          </span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Reports durchsuchen …"
            className="ghost-search__input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="ghost-search__clear"
            >
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" width="10" height="10">
                <line x1="2" y1="2" x2="10" y2="10" /><line x1="10" y1="2" x2="2" y2="10" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Sortierung ──────────────────────────────────────────── */}
      <div data-section="doc-sort" className="ghost-sort">
        {([
          { key: 'updated' as const, label: 'Geändert' },
          { key: 'created' as const, label: 'Erstellt' },
          { key: 'title' as const, label: 'Name' },
        ]).map(opt => {
          const isActive = sortBy === opt.key
          return (
            <button
              key={opt.key}
              onClick={() => {
                if (isActive) {
                  setSortDir(d => d === 'desc' ? 'asc' : 'desc')
                } else {
                  setSortBy(opt.key)
                  setSortDir(opt.key === 'title' ? 'asc' : 'desc')
                }
              }}
              className={`ghost-sort__btn ${isActive ? 'ghost-sort__btn--active' : ''}`}
            >
              {opt.label}
              {isActive && (
                <svg viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" width="7" height="7">
                  {sortDir === 'desc'
                    ? <polyline points="1,3 4,6 7,3" />
                    : <polyline points="1,5 4,2 7,5" />
                  }
                </svg>
              )}
            </button>
          )
        })}
      </div>

      {/* Dokument-Liste */}
      <div data-section="doc-items" className="ghost-list">
        {filteredDocs.length === 0 && (
          <div className="ghost-empty">
            Noch keine Dokumente. Klicke auf "+" um zu starten.
          </div>
        )}

        {filteredDocs.map(doc => {
          const date = new Date(doc.updatedAt).toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: '2-digit',
          })
          const isHovered = hoveredItemId === doc.id

          return (
            <div
              key={doc.id}
              data-component="DocumentListItem"
              data-document-id={doc.id}
              onClick={() => onOpenDocument ? onOpenDocument(doc.id) : openDocumentViewer(doc.id)}
              onMouseEnter={() => setHoveredItemId(doc.id)}
              onMouseLeave={() => setHoveredItemId(null)}
              className={`ghost-item ${isHovered ? 'ghost-item--active' : ''}`}
            >
              <div className="ghost-item__content">
                <div className="ghost-item__title">
                  {doc.title}
                </div>
                <div className="ghost-item__meta">
                  <span>{date}</span>
                </div>
              </div>

              {(doc.linkedAnnotationIds || []).length > 0 && (
                <div className="ghost-item__aside">
                  <span className="ghost-item__badge">
                    {(doc.linkedAnnotationIds || []).length} Notiz{(doc.linkedAnnotationIds || []).length !== 1 ? 'en' : ''}
                  </span>
                </div>
              )}

              {/* Hover-Actions */}
              {isHovered ? (
                <div
                  data-section="list-item-actions"
                  className="ghost-actions"
                  onClick={e => e.stopPropagation()}
                >
                  <DocListAction
                    title="Bearbeiten"
                    onClick={() => {
                      if (onOpenDocument) onOpenDocument(doc.id)
                      openDocumentEditor(doc.id)
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                  </DocListAction>
                  <DocListAction
                    title="Duplizieren"
                    onClick={(e) => handleDuplicate(doc, e as React.MouseEvent)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </DocListAction>
                  <DocListAction
                    title="Löschen"
                    danger
                    onClick={(e) => handleDelete(doc, e as React.MouseEvent)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </DocListAction>
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── DocListAction ────────────────────────────────────────────────────────────

function DocListAction({
  title,
  danger,
  onClick,
  children,
}: {
  title: string
  danger?: boolean
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}) {
  return (
    <button
      data-element={`doc-action-${title.toLowerCase()}`}
      title={title}
      onClick={e => { e.stopPropagation(); onClick(e) }}
      className={`ghost-action-btn ${danger ? 'ghost-action-btn--danger' : ''}`}
    >
      {children}
    </button>
  )
}
